import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { ChatMarkdownContent } from './ChatMarkdownContent';
import { CHAT_USER_MESSAGE_BUBBLE_CLASSNAME } from './chat-ui-classes';
import { X, ChevronLeft, Plus, Loader2, Send } from 'lucide-react';
import { API_BASE } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { useCreditCheck } from '../hooks/useCreditCheck';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useToast } from '../contexts/ToastContext';
import {
  fetchSuggestionIntent,
  formatScopeLabel,
  runChatSuggestions,
  type SuggestionIntentResult,
} from '../utils/chat-suggestions';
import { useRightSidebar } from '../contexts/RightSidebarContext';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { getDocumentMarkdownText } from '../utils/document-plain-text';
import {
  formatMessageDayLabel,
  getCalendarDayKey,
  sortThreadsOldestFirst,
} from '../utils/chat-threads';

type ChatMessageMetadata = {
  suggestionRun?: {
    status: 'completed' | 'failed';
    suggestionCount: number;
    creditsUsed: number;
    firstSuggestionIds: string[];
  };
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  metadata?: ChatMessageMetadata | null;
};

type ChatThread = {
  id: string;
  documentId: string;
  userId: string;
  title: string | null;
  useDocumentSummary?: boolean;
  createdAt: string;
};

type PendingSuggestionIntent = {
  question: string;
  userMessageId: string;
  threadId: string;
  isSelectedText: boolean;
  selectedText: string;
  selectionFromPos: number | null;
  selectionToPos: number | null;
  intentResult: SuggestionIntentResult;
};

type DocumentChatProps = {
  editor: Editor | null;
  documentId?: string | null;
  shareToken?: string | null;
  isVisible?: boolean;
  hideHeading?: boolean;
  navigateToThreadId?: string | null;
  navigateToMessageId?: string | null;
  focusRequest?: number;
  onNavigationHandled?: () => void;
  onNavigateToSuggestions?: () => void;
  onLoadSuggestions?: (options?: { sortByDocumentOrder?: boolean }) => void | Promise<void>;
  onHighlightSuggestions?: () => void;
  onScrollToSuggestionById?: (suggestionId: string) => void;
};

const SELECTED_TEXT_TOOLTIP_EXCERPT_LENGTH = 25;

function formatSelectedTextTooltipExcerpt(text: string): string {
  if (text.length <= SELECTED_TEXT_TOOLTIP_EXCERPT_LENGTH * 2) {
    return text;
  }

  const start = text.slice(0, SELECTED_TEXT_TOOLTIP_EXCERPT_LENGTH);
  const end = text.slice(-SELECTED_TEXT_TOOLTIP_EXCERPT_LENGTH);
  return `${start} (...) ${end}`;
}

async function fetchLatestUserMessageId(
  authToken: string,
  threadId: string,
  question: string
): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE}/api/chat/threads/${threadId}/messages`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const userMessages = (data.messages || []).filter(
      (message: ChatMessage) => message.role === 'user'
    ) as ChatMessage[];
    const exactMatch = [...userMessages].reverse().find((message) => message.content === question);
    return exactMatch?.id ?? userMessages[userMessages.length - 1]?.id ?? null;
  } catch {
    return null;
  }
}

export const DocumentChat: React.FC<DocumentChatProps> = ({
  editor,
  documentId = null,
  shareToken = null,
  isVisible = false,
  hideHeading = false,
  navigateToThreadId = null,
  navigateToMessageId = null,
  focusRequest = 0,
  onNavigationHandled,
  onNavigateToSuggestions,
  onLoadSuggestions,
  onHighlightSuggestions,
  onScrollToSuggestionById,
}) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast } = useToast();
  const { chatSelectionContext, clearChatSelection, closeChat } = useRightSidebar();
  const { checkCredits, CreditLimitModalComponent } = useCreditCheck();
  const { refresh: refreshOnboarding } = useOnboarding();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [chatQuestion, setChatQuestion] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentAnswer, setCurrentAnswer] = useState<string>('');
  const [isLoadingChat, setIsLoadingChat] = useState<boolean>(false);
  const [isLoadingThreads, setIsLoadingThreads] = useState<boolean>(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const [useDocumentSummary, setUseDocumentSummary] = useState<boolean>(true);
  const [pendingIntent, setPendingIntent] = useState<PendingSuggestionIntent | null>(null);
  const [isRunningSuggestions, setIsRunningSuggestions] = useState<boolean>(false);
  const [suggestionProgress, setSuggestionProgress] = useState<string>('');
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const threadListEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedText = chatSelectionContext?.text ?? '';
  const hasChatSelection = chatSelectionContext !== null;

  useEffect(() => {
    if (chatSelectionContext) {
      setUseDocumentSummary(false);
    }
  }, [chatSelectionContext]);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollThreadListToBottom = (): void => {
    threadListEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentAnswer, suggestionProgress]);

  useEffect(() => {
    if (isVisible && documentId && documentId !== 'new' && !shareToken) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isVisible, documentId, shareToken]);

  useEffect(() => {
    if (!isVisible || focusRequest === 0) {
      return;
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [focusRequest, isVisible]);

  useEffect(() => {
    if (!navigateToThreadId) {
      return;
    }
    setActiveThreadId(navigateToThreadId);
    const navigatedThread = threads.find((thread) => thread.id === navigateToThreadId);
    if (navigatedThread) {
      setUseDocumentSummary(navigatedThread.useDocumentSummary !== false);
    }
    if (navigateToMessageId) {
      requestAnimationFrame(() => {
        messageRefs.current[navigateToMessageId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onNavigationHandled?.();
      });
    } else {
      onNavigationHandled?.();
    }
  }, [navigateToThreadId, navigateToMessageId, onNavigationHandled, threads]);

  useEffect(() => {
    if (!isVisible || !documentId || documentId === 'new') {
      setActiveThreadId(null);
      setMessages([]);
      return;
    }

    if (navigateToThreadId) {
      return;
    }

    setActiveThreadId(null);
    if (!chatSelectionContext) {
      setUseDocumentSummary(true);
    }

    const loadThreads = async (): Promise<void> => {
      if (!token || shareToken) return;

      setIsLoadingThreads(true);
      try {
        const authToken = shareToken || token;
        const response = await fetch(`${API_BASE}/api/chat/threads/${documentId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setThreads(sortThreadsOldestFirst(data.threads || []));
        }
      } catch (err) {
        console.error('Error loading chat threads:', err);
      } finally {
        setIsLoadingThreads(false);
      }
    };

    loadThreads();
  }, [documentId, token, shareToken, isVisible, navigateToThreadId, chatSelectionContext]);

  useEffect(() => {
    if (isLoadingChat) return;

    const loadMessages = async (): Promise<void> => {
      if (!activeThreadId || !token || shareToken) {
        setMessages([]);
        return;
      }

      setIsLoadingMessages(true);
      try {
        const authToken = shareToken || token;
        const response = await fetch(`${API_BASE}/api/chat/threads/${activeThreadId}/messages`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setMessages((data.messages || []).map((m: ChatMessage) => ({
            ...m,
            createdAt: typeof m.createdAt === 'string' ? m.createdAt : new Date(m.createdAt).toISOString(),
          })));
          if (data.thread) {
            setUseDocumentSummary(data.thread.useDocumentSummary !== false);
            setThreads((prev) => prev.map((thread) => (
              thread.id === activeThreadId
                ? { ...thread, useDocumentSummary: data.thread.useDocumentSummary !== false }
                : thread
            )));
          }
        }
      } catch (err) {
        console.error('Error loading thread messages:', err);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadMessages();
  }, [activeThreadId, token, shareToken, isLoadingChat]);

  useEffect(() => {
    if (
      !isVisible
      || activeThreadId
      || !documentId
      || documentId === 'new'
      || isLoadingThreads
      || threads.length === 0
    ) {
      return;
    }
    requestAnimationFrame(() => {
      scrollThreadListToBottom();
    });
  }, [isVisible, activeThreadId, documentId, isLoadingThreads, threads.length]);

  const updateThreadDocumentSummaryPreference = useCallback(async (value: boolean): Promise<void> => {
    setUseDocumentSummary(value);

    if (!activeThreadId || !token || shareToken) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/chat/threads/${activeThreadId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ useDocumentSummary: value }),
      });
      if (response.ok) {
        setThreads((prev) => prev.map((thread) => (
          thread.id === activeThreadId
            ? { ...thread, useDocumentSummary: value }
            : thread
        )));
      }
    } catch (err) {
      console.error('Error updating chat thread summary preference:', err);
    }
  }, [activeThreadId, token, shareToken]);

  const handleDismissDocumentSummary = useCallback(async (): Promise<void> => {
    await updateThreadDocumentSummaryPreference(false);
  }, [updateThreadDocumentSummaryPreference]);

  const handleAttachDocumentSummary = useCallback(async (): Promise<void> => {
    await updateThreadDocumentSummaryPreference(true);
  }, [updateThreadDocumentSummaryPreference]);

  const checkSuggestionIntent = useCallback(async (params: {
    question: string;
    userMessageId: string;
    threadId: string;
    isSelectedText: boolean;
    selectedText: string;
    selectionFromPos: number | null;
    selectionToPos: number | null;
  }): Promise<void> => {
    if (!token || !documentId) {
      return;
    }

    try {
      const intentResult = await fetchSuggestionIntent({
        token,
        question: params.question,
        documentId,
        threadId: params.threadId,
        userMessageId: params.userMessageId,
        isSelectedText: params.isSelectedText,
        selectedText: params.selectedText,
      });

      if (intentResult.wantsSuggestions && intentResult.intent) {
        setPendingIntent({
          question: params.question,
          userMessageId: params.userMessageId,
          threadId: params.threadId,
          isSelectedText: params.isSelectedText,
          selectedText: params.selectedText,
          selectionFromPos: params.selectionFromPos,
          selectionToPos: params.selectionToPos,
          intentResult,
        });
      }
    } catch (err) {
      console.error('Error checking suggestion intent:', err);
    }
  }, [token, documentId]);

  const handleConfirmSuggestionRun = async (): Promise<void> => {
    if (!pendingIntent || !token || !documentId || !pendingIntent.intentResult.intent) {
      return;
    }

    const estimatedCredits = pendingIntent.intentResult.estimatedCredits ?? 0;
    const hasCredits = await checkCredits(estimatedCredits);
    if (!hasCredits) {
      return;
    }

    const { intent } = pendingIntent.intentResult;
    setPendingIntent(null);
    setIsRunningSuggestions(true);
    setSuggestionProgress(t('chat_suggestions_starting'));

    try {
      const result = await runChatSuggestions({
        token,
        threadId: pendingIntent.threadId,
        userMessageId: pendingIntent.userMessageId,
        documentId,
        question: pendingIntent.question,
        taskSummary: intent.taskSummary,
        scope: intent.scope,
        scopeLabel: pendingIntent.intentResult.scopeLabel,
        chapterTitles: intent.chapterTitles,
        isSelectedText: pendingIntent.isSelectedText,
        selectedText: pendingIntent.selectedText,
        selectionFromPos: pendingIntent.selectionFromPos ?? undefined,
        selectionToPos: pendingIntent.selectionToPos ?? undefined,
        needsCharacterContext: intent.needsCharacterContext,
        needsLocationContext: intent.needsLocationContext,
        requiresExhaustiveCoverage: intent.requiresExhaustiveCoverage,
        onEvent: (event) => {
          if (event.type === 'progress') {
            setSuggestionProgress(
              t('chat_suggestions_progress')
                .replace('{chunk}', String(event.chunk))
                .replace('{total}', String(event.totalChunks))
                .replace('{count}', String(event.issuesSoFar))
            );
          }
          if (event.type === 'issue') {
            void onLoadSuggestions?.();
            onHighlightSuggestions?.();
          }
        },
      });

      setMessages((prev) => prev.map((message) => {
        if (message.id !== pendingIntent.userMessageId) {
          return message;
        }
        return {
          ...message,
          metadata: {
            suggestionRun: {
              status: 'completed',
              suggestionCount: result.suggestionCount,
              creditsUsed: result.creditsUsed,
              firstSuggestionIds: result.firstSuggestionIds,
            },
          },
        };
      }));

      await onLoadSuggestions?.();
      onHighlightSuggestions?.();
    } catch (err) {
      console.error('Error running chat suggestions:', err);
      setSuggestionProgress(t('chat_suggestions_error'));
    } finally {
      setIsRunningSuggestions(false);
      setSuggestionProgress('');
    }
  };

  const handleChatSubmit = async (): Promise<void> => {
    if (!editor || !chatQuestion.trim() || isLoadingChat || isRunningSuggestions) return;

    if (shareToken) return;

    const hasCredits = await checkCredits();
    if (!hasCredits) return;

    if (!documentId || documentId === 'new') return;

    const authToken = shareToken || token;
    if (!authToken) throw new Error('No authentication token');

    const hasSelection = hasChatSelection;
    const selectionText = chatSelectionContext?.text ?? '';
    const selectionFromPos = chatSelectionContext?.from ?? null;
    const selectionToPos = chatSelectionContext?.to ?? null;

    setIsLoadingChat(true);
    setCurrentAnswer('');

    const tempUserMessageId = `temp-${Date.now()}`;
    const userMessage: ChatMessage = {
      id: tempUserMessageId,
      role: 'user',
      content: chatQuestion,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const questionToSend = chatQuestion;
    setChatQuestion('');
    requestAnimationFrame(() => inputRef.current?.focus());

    try {
      const text = hasSelection
        ? selectionText
        : useDocumentSummary
          ? getDocumentMarkdownText(editor.state.doc)
          : '.';

      const body: Record<string, unknown> = {
        text,
        question: questionToSend,
        isSelectedText: hasSelection,
        useDocumentSummary: hasSelection ? true : useDocumentSummary,
      };
      if (activeThreadId) {
        body.threadId = activeThreadId;
      } else {
        body.documentId = documentId;
      }

      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to get chat response');
      if (!response.body) throw new Error('No response body');

      const newThreadId = response.headers.get('X-Chat-Thread-Id');
      const userMessageId = response.headers.get('X-Chat-User-Message-Id');
      const resolvedThreadId = newThreadId || activeThreadId;

      if (newThreadId && !activeThreadId) {
        setActiveThreadId(newThreadId);
        setThreads((prev) => {
          const newThread: ChatThread = {
            id: newThreadId,
            documentId,
            userId: '',
            title: questionToSend.substring(0, 200),
            useDocumentSummary: hasSelection ? true : useDocumentSummary,
            createdAt: new Date().toISOString(),
          };
          return sortThreadsOldestFirst([...prev, newThread]);
        });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullAnswer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullAnswer += chunk;
        setCurrentAnswer(fullAnswer);
      }

      let resolvedUserMessageId = userMessageId || tempUserMessageId;
      const assistantMessage: ChatMessage = {
        id: `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content: fullAnswer,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => {
        const updated = prev.map((message) => (
          message.id === tempUserMessageId
            ? { ...message, id: resolvedUserMessageId }
            : message
        ));
        return [...updated, assistantMessage];
      });
      setCurrentAnswer('');
      void refreshOnboarding();

      if (resolvedThreadId && !userMessageId) {
        const fetchedUserMessageId = await fetchLatestUserMessageId(authToken, resolvedThreadId, questionToSend);
        if (fetchedUserMessageId) {
          resolvedUserMessageId = fetchedUserMessageId;
          setMessages((prev) => prev.map((message) => (
            message.id === tempUserMessageId || message.content === questionToSend
              ? { ...message, id: fetchedUserMessageId }
              : message
          )));
        }
      }

      if (resolvedThreadId && resolvedUserMessageId && !resolvedUserMessageId.startsWith('temp-')) {
        void checkSuggestionIntent({
          question: questionToSend,
          userMessageId: resolvedUserMessageId,
          threadId: resolvedThreadId,
          isSelectedText: hasSelection,
          selectedText: selectionText,
          selectionFromPos,
          selectionToPos,
        });
      }
    } catch (err) {
      console.error('Error getting chat response:', err);
      setMessages((prev) => prev.slice(0, -1));
      setCurrentAnswer('Error getting response. Please try again.');
    } finally {
      setIsLoadingChat(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const formatThreadTitle = (thread: ChatThread): string => {
    if (thread.title && thread.title.trim()) {
      return thread.title.length > 60 ? `${thread.title.substring(0, 60)}...` : thread.title;
    }
    return t('new_chat');
  };

  const handleDeleteThread = async (threadId: string): Promise<void> => {
    if (!token || shareToken || deletingThreadId) {
      return;
    }

    setDeletingThreadId(threadId);
    try {
      const response = await fetch(`${API_BASE}/api/chat/threads/${threadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to delete chat thread');
      }
      setThreads((prev) => prev.filter((thread) => thread.id !== threadId));
      if (activeThreadId === threadId) {
        setActiveThreadId(null);
        setMessages([]);
        setCurrentAnswer('');
      }
      showToast(t('chat_deleted'));
    } catch (err) {
      console.error('Error deleting chat thread:', err);
      showErrorToast(t('failed_to_delete_chat'));
    } finally {
      setDeletingThreadId(null);
    }
  };

  const renderSuggestionSummary = (message: ChatMessage): React.ReactNode => {
    const run = message.metadata?.suggestionRun;
    if (!run || run.status !== 'completed') {
      return null;
    }

    return (
      <div className="mt-2 max-w-[80%] rounded-lg border border-border bg-muted/40 p-3 text-sm">
        <p>
          {t('chat_suggestions_summary')
            .replace('{count}', String(run.suggestionCount))
            .replace('{credits}', String(run.creditsUsed))}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-7"
            onClick={() => onNavigateToSuggestions?.()}
          >
            {t('chat_suggestions_view')}
          </Button>
          {run.firstSuggestionIds[0] && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => onScrollToSuggestionById?.(run.firstSuggestionIds[0])}
            >
              {t('chat_suggestions_go_to_first')}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const showThreadList = !activeThreadId && (documentId && documentId !== 'new');
  const canChat = documentId && documentId !== 'new' && !shareToken;
  const confirmScopeLabel = pendingIntent?.intentResult.scopeLabel
    ? formatScopeLabel(pendingIntent.intentResult.scopeLabel, t)
    : '';
  const isSendBusy = isLoadingChat || isRunningSuggestions;

  return (
    <>
      {CreditLimitModalComponent}
      <Dialog open={pendingIntent !== null} onOpenChange={(open) => !open && setPendingIntent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chat_suggestions_confirm_title')}</DialogTitle>
            <DialogDescription>{t('chat_suggestions_confirm_description')}</DialogDescription>
          </DialogHeader>
          {pendingIntent?.intentResult.intent && (
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">{t('chat_suggestions_task')}:</span> {pendingIntent.intentResult.intent.taskSummary}</p>
              <p><span className="font-medium">{t('chat_suggestions_scope')}:</span> {confirmScopeLabel}</p>
              <p><span className="font-medium">{t('chat_suggestions_estimated_credits')}:</span> {pendingIntent.intentResult.estimatedCredits ?? 0}</p>
              <p><span className="font-medium">{t('current_balance')}:</span> {pendingIntent.intentResult.balance ?? 0}</p>
              {pendingIntent.intentResult.canAfford === false && (
                <p className="text-destructive">{t('chat_suggestions_insufficient_credits')}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingIntent(null)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={() => void handleConfirmSuggestionRun()}
              disabled={pendingIntent?.intentResult.canAfford === false || isRunningSuggestions}
            >
              {t('chat_suggestions_confirm_run')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col h-full p-0 md:p-4">
        {(activeThreadId || !hideHeading) && (
          <div className="flex items-center gap-2 mb-4 flex-shrink-0">
            {activeThreadId && (
              <button
                onClick={() => { setActiveThreadId(null); setMessages([]); setCurrentAnswer(''); }}
                className="p-1 rounded hover:bg-muted"
                aria-label={t('back')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {!hideHeading && <h3 className="text-lg font-bold text-foreground">{t('chat_with_document')}</h3>}
          </div>
        )}

        <div className="flex-1 overflow-y-auto mb-4 p-2 md:p-4 bg-muted rounded-lg min-h-[150px] flex flex-col">
          {showThreadList ? (
            <>
              {threads.length > 0 && (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="text-xs font-medium text-muted-foreground dark:text-foreground/80 mb-2 flex-shrink-0">{t('previous_conversations')}</div>
                  {isLoadingThreads ? (
                    <div className="text-muted-foreground dark:text-foreground/80 text-sm">{t('loading') || 'Loading...'}</div>
                  ) : (
                    <div className="space-y-1 flex-1 min-h-0 overflow-y-auto">
                      {threads.map((thread) => (
                        <div
                          key={thread.id}
                          className="group/chat-thread flex items-center gap-1 w-full rounded-lg bg-background border border-border hover:bg-muted/80 transition-colors"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setActiveThreadId(thread.id);
                              setUseDocumentSummary(thread.useDocumentSummary !== false);
                            }}
                            className="flex-1 min-w-0 p-3 text-left"
                          >
                            <span className="text-sm font-medium truncate block w-full text-foreground">
                              {formatThreadTitle(thread)}
                            </span>
                          </button>
                          <div className="opacity-0 group-hover/chat-thread:opacity-100 focus-within:opacity-100 pr-1.5 flex-shrink-0 transition-opacity">
                            <RotatingDeleteButton
                              onClick={() => {
                                void handleDeleteThread(thread.id);
                              }}
                              tooltipText={t('delete_chat_tooltip')}
                              ariaLabel={t('delete_chat')}
                              disabled={deletingThreadId === thread.id}
                              className="h-8 w-8 p-0"
                            />
                          </div>
                        </div>
                      ))}
                      <div ref={threadListEndRef} />
                    </div>
                  )}
                </div>
              )}
              {threads.length === 0 && !isLoadingThreads && (
                <div className="text-muted-foreground dark:text-foreground/80 text-sm flex-1">{t('no_threads_yet')}</div>
              )}
            </>
          ) : (
            <>
              {isLoadingMessages ? (
                <div className="text-muted-foreground dark:text-foreground/80">{t('loading') || 'Loading...'}</div>
              ) : messages.length === 0 && !currentAnswer ? (
                <div className="text-muted-foreground dark:text-foreground/80">{t('ask_question')}</div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    const previousMessage = index > 0 ? messages[index - 1] : null;
                    const showSuggestionSummary = message.role === 'assistant'
                      && previousMessage?.role === 'user';
                    const showDaySeparator = !previousMessage
                      || getCalendarDayKey(previousMessage.createdAt) !== getCalendarDayKey(message.createdAt);
                    const dayLabel = showDaySeparator
                      ? formatMessageDayLabel(message.createdAt, t('today'), t('yesterday'))
                      : '';

                    return (
                    <React.Fragment key={message.id}>
                      {showDaySeparator && dayLabel && (
                        <div className="flex justify-center">
                          <span className="text-xs text-muted-foreground dark:text-foreground/70">
                            {dayLabel}
                          </span>
                        </div>
                      )}
                      <div
                        ref={(element) => { messageRefs.current[message.id] = element; }}
                        className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            message.role === 'user'
                              ? CHAT_USER_MESSAGE_BUBBLE_CLASSNAME
                              : 'bg-background border border-border'
                          }`}
                        >
                          {message.role === 'user' ? (
                            <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                          ) : (
                            <ChatMarkdownContent content={message.content} />
                          )}
                        </div>
                        {showSuggestionSummary && renderSuggestionSummary(previousMessage)}
                      </div>
                    </React.Fragment>
                    );
                  })}
                  {currentAnswer && (
                    <div className="flex flex-col items-start">
                      <div className="max-w-[80%] rounded-lg p-3 bg-background border border-border">
                        <ChatMarkdownContent content={currentAnswer} />
                      </div>
                    </div>
                  )}
                  {suggestionProgress && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground dark:text-foreground/80">
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                      <span>{suggestionProgress}</span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </>
          )}
        </div>

        {selectedText && (
          <div className="mb-2">
            <div className="mb-1 text-xs text-muted-foreground dark:text-foreground/80">{t('chat_about_selected_text')}</div>
            <TooltipProvider>
              <Badge className="bg-muted text-foreground dark:bg-secondary dark:text-secondary-foreground text-sm flex items-center gap-2 pr-1 border border-border">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-1 cursor-default">
                      {selectedText.length > 50 ? `${selectedText.substring(0, 50)}...` : selectedText}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>{t('chat_about_selected_text_tooltip')}</p>
                    <p className="mt-2 whitespace-pre-wrap text-muted-foreground">
                      {formatSelectedTextTooltipExcerpt(selectedText)}
                    </p>
                  </TooltipContent>
                </Tooltip>
                <button
                  onClick={() => {
                    clearChatSelection();
                    if (editor) {
                      const { from } = editor.state.selection;
                      editor.commands.setTextSelection(from);
                      editor.commands.focus();
                    }
                  }}
                  className="hover:bg-muted/80 dark:hover:bg-secondary/80 rounded p-0.5 transition-colors"
                  aria-label={t('clear_selection')}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </TooltipProvider>
          </div>
        )}
        {!selectedText && useDocumentSummary && (
          <div className="mb-2">
            <Badge className="bg-scribane-green text-scribane-black text-sm flex items-center gap-2 pr-1">
              <span className="flex-1">{t('document_summary_context')}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => {
                        void handleDismissDocumentSummary();
                      }}
                      className="hover:bg-scribane-green/80 rounded p-0.5 transition-colors"
                      aria-label={t('chat_dismiss_document_summary_tooltip')}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    {t('chat_dismiss_document_summary_tooltip')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Badge>
          </div>
        )}
        {!selectedText && !useDocumentSummary && (
          <div className="mb-2 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-auto gap-1.5 whitespace-normal py-1.5 text-left font-normal"
              onClick={() => {
                void handleAttachDocumentSummary();
              }}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              {t('chat_attach_document_summary')}
            </Button>
          </div>
        )}

        <div className="flex gap-2 flex-shrink-0 items-stretch">
          <Input
            ref={inputRef}
            type="text"
            value={chatQuestion}
            onChange={(e) => setChatQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                closeChat();
                editor?.commands.focus();
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey && !isLoadingChat && !isRunningSuggestions) {
                e.preventDefault();
                handleChatSubmit();
              }
            }}
            placeholder={t('ask_question_placeholder')}
            className="flex-1 rounded-lg bg-background text-foreground h-9 placeholder:text-muted-foreground dark:placeholder:text-foreground/70"
            disabled={isRunningSuggestions}
          />
          <Button
            onClick={handleChatSubmit}
            disabled={isSendBusy || !chatQuestion.trim() || !canChat}
            className="rounded-lg px-6 h-9 gap-2 group/chat-send"
          >
            {isSendBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4 transition-transform duration-200 group-hover/chat-send:scale-110" aria-hidden />
            )}
            {isLoadingChat ? t('sending') : t('send')}
          </Button>
        </div>
      </div>
    </>
  );
};
